import React, { useEffect, useMemo } from 'react'

import { FormProvider, useForm } from 'react-hook-form'

import FHeading1 from 'components/BaseComponents/Text/FHeading1'

import FButton from 'components/BaseComponents/FButton'
import FParagraph from 'components/BaseComponents/Text/FParagraph'

import { useSTheme } from 'styles/theme'

import FNeedHelpCTA from 'components/BaseComponents/FNeedHelpCTA'
import { useTranslation } from 'i18n'
import { input } from 'components/BaseComponents/constants'
import { StepWizardChildProps } from 'react-step-wizard'
import FInputRadioGroup from '../../../../../components/BaseComponents/FInputRadioGroup'
import { Box } from 'rebass'
import { useBenefitsAnswerQueryQuery, useBenefitsAnswersMutation } from '../../../../../types/graphql'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { ga4_useTitle } from 'utils/ga4'
import { LBTextHeader, LBTextSubHeader } from 'containers/Apply/steps/ApplicationStepLBIntroduction'

interface Props {
    onPrevious: () => void
    onFinish: () => void
    personId: any
    backwardsStep?: boolean
    onAcpOptionOne: (value: boolean) => void
}

const ACPBenefitsHow: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const form = useForm({ shouldFocusError: false })
    const { t } = useTranslation()
    const theme = useSTheme()
    const [saveBenefitAnswer, { loading }] = useBenefitsAnswersMutation()
    const { data, loading: queryLoading } = useBenefitsAnswerQueryQuery({
        variables: { personId: props.personId },
        fetchPolicy: 'network-only',
    })

    ga4_useTitle('ACP how')

    const onFormSubmit = async (values) => {
        await saveBenefitAnswer({
            variables: {
                answers: [
                    {
                        personId: props.personId,
                        category: 'ACP_HOW',
                        answer: values.acp_how,
                    },
                ],
            },
        })
        props.onAcpOptionOne(values.acp_program === 'acp_ssi_wic_lifeline_medicaid_snap_school')
        return props.onFinish()
    }

    useEffect(() => {
        if (data) {
            const savedValue = data.benefitsAnswers.find((answer: any) => answer.category === 'ACP_HOW')?.answer
            form.setValue('acp_how', savedValue)
        }
    }, [data])

    const radioDataArray = useMemo(() => {
        return [
            {
                text: t('acp_how_income_verification'),
                id: 'acp_how_income_verification',
            },
            {
                text: t('acp_ssi_wic_lifeline_medicaid_snap_school'),
                id: 'acp_ssi_wic_lifeline_medicaid_snap_school',
            },
            {
                text: t('acp_veterans_public_housing_pbra_section_8'),
                id: 'acp_veterans_public_housing_pbra_section_8',
            },
        ]
    }, [t])
    return (
        <FormProvider {...form}>
            <FButtonPrevious onClick={props.onPrevious} />
            <LBTextHeader
                sx={{
                    textAlign: 'left',
                    fontSize: '32px',
                    color: theme.colors.secondaryColor,
                    lineHeight: '120%',
                    fontFamily: 'Raleway',
                    fontWeight: '900',
                }}
            >
                {t('acp_how_title')}
            </LBTextHeader>
            <LBTextSubHeader
                color={theme.colors.blackLabelfont}
                fontFamily={theme.fonts.longBeach.body}
                dangerouslySetInnerHTML={{
                    __html: t('acp_how_question'),
                }}
            />

            <Box as="form" onSubmit={form.handleSubmit(onFormSubmit)}>
                <FInputRadioGroup
                    radioDataArray={radioDataArray}
                    name={'acp_how'}
                    id={'acp_how'}
                    minimumSelection={1}
                />
                <FButton
                    buttonType={'filled'}
                    loading={loading}
                    type={'submit'}
                    mt={input.desktop.marginTop}
                    ga4_key={t('continue', { lang: 'en' })}
                >
                    {t('save_and_continue')}
                </FButton>
            </Box>
        </FormProvider>
    )
}

export default ACPBenefitsHow
